import { useEffect, useState } from 'react';

import { listMembers, listPlaylists } from './graphql/queries';
import { updatePlaylist } from './graphql/mutations';
import { membersRoles } from '../../../const';

export default ({
  handleHistory,
  managerSelected,
  organizationId,
  setPlaylistError,
  useLazyGraphQLQuery,
  useGraphQLMutation,
}) => {
  const [listOwnedPlaylists, setListOwnedPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [updatePlaylistContentMutation] = useGraphQLMutation(updatePlaylist);
  const [updatePlaylistMutation] = useGraphQLMutation(updatePlaylist);

  const limitMembers = 100;
  const limitPlaylists = 100;

  const [listMembersQuery, listMembersResponse] =
    useLazyGraphQLQuery(listMembers);
  const [listPlaylistsQuery, listPlaylistsResponse] =
    useLazyGraphQLQuery(listPlaylists);

  const selectPlaylistHandler = (id, disableToggle) => {
    if ((id === selectedPlaylist?.id && !disableToggle) || id === null) {
      setSelectedPlaylist(null);
      // TODO: Check why the type is gone
      handleHistory(`manager=${managerSelected}`);
    } else {
      setSelectedPlaylist(
        listOwnedPlaylists.find((playlist) => playlist.id === id)
      );
      handleHistory(`manager=${managerSelected}&playlist=${id}`);
    }
  };

  const playlistContentOrderHandler = (sortedCourses) => {
    const input = {
      id: selectedPlaylist.id,
      courses: {
        update: sortedCourses.map((course, index) => ({
          id: course.id,
          ordinal: index + 1,
        })),
      },
    };

    updatePlaylistContentMutation({
      variables: {
        input,
      },
    });

    return true;
  };

  const playlistOrderHandler = (newPosition, playlistId, newOrder) => {
    const playlist = listOwnedPlaylists.find((p) => p.id === playlistId);
    if (!playlist) return;

    updatePlaylistMutation({
      variables: {
        input: {
          id: playlistId,
          ordinal: newPosition,
        },
      },
      update(cache) {
        cache.modify({
          fields: {
            listPlaylists(existing = []) {
              return newOrder.reduce((acc, curr) => {
                const existingItem = existing.find(
                  (ref) => cache.identify(curr) === ref.__ref
                );
                if (existingItem) {
                  acc.push(existingItem);
                }
                return acc;
              }, []);
            },
          },
        });
      },
      onError: (error) => {
        console.error('Failed to update playlist order:', error);
        setPlaylistError('Failed to update playlist order. Please try again.');
      },
    });
  };

  useEffect(() => {
    selectPlaylistHandler(null);

    const filter = {
      administrativeView: true,
      status: ['DRAFT', 'PUBLISHED', 'PAUSED', 'TEMPLATE'],
    };

    if (organizationId === 'global') {
      filter.isGlobal = true;
    } else {
      filter.organizationId = organizationId;
    }
    if (managerSelected !== 'all') {
      filter.ownerId = managerSelected;
    }

    listPlaylistsQuery({
      variables: {
        filter,
        pagination: {
          limit: limitPlaylists,
          offset: 0,
        },
      },
    });
  }, [managerSelected, organizationId]);

  useEffect(() => {
    if (organizationId !== 'global') {
      listMembersQuery({
        variables: {
          filter: {
            organizationId: {
              eq: organizationId,
            },
            role: [membersRoles.admin, membersRoles.manager],
          },
          pagination: {
            limit: limitMembers,
            offset: 0,
          },
        },
      });
    }
  }, [organizationId]);

  useEffect(() => {
    const { data, loading, error } = listPlaylistsResponse;

    if (data && !loading) {
      if (
        data.listPlaylists &&
        data.listPlaylists.length > 0 &&
        data.listPlaylists.length % limitPlaylists === 0
      ) {
        listPlaylistsResponse.fetchMore({
          variables: {
            pagination: {
              limit: limitPlaylists,
              offset: data.listPlaylists.length,
            },
          },
        });
      }
      setListOwnedPlaylists(data.listPlaylists || []);
    } else if (error) {
      setPlaylistError(true);
      setListOwnedPlaylists([]);
    }
  }, [
    listPlaylistsResponse.data,
    listPlaylistsResponse.loading,
    listPlaylistsResponse.error,
  ]);

  useEffect(() => {
    const { data, loading } = listMembersResponse;

    if (data && !loading) {
      if (
        data.listMembers &&
        data.listMembers.length > 0 &&
        data.listMembers.length % limitMembers === 0
      ) {
        listMembersResponse.fetchMore({
          variables: {
            pagination: {
              limit: limitMembers,
              offset: data.listMembers.length,
            },
          },
        });
      }
    }
  }, [listMembersResponse.data, listMembersResponse.loading]);

  useEffect(() => {
    if (selectedPlaylist) {
      const playlist = listOwnedPlaylists.find(
        (playlist) => playlist.id === selectedPlaylist.id
      );
      setSelectedPlaylist(playlist);
    }
  }, [listOwnedPlaylists]);

  return {
    listOwnedPlaylists,
    loadingPlaylists: listPlaylistsResponse.loading,
    playlistContentOrderHandler,
    playlistManagers: listMembersResponse.data?.listMembers || [],
    playlistOrderHandler,
    selectedPlaylist,
    selectPlaylistHandler,
  };
};
